import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { FiEye, FiHome, FiPaperclip, FiX } from 'react-icons/fi';
import { API_URL, BASE_URL } from '../Api/api';
import { getAuthToken } from '../Api/authToken';
import prepareChatFile, { isPdfAttachment } from '../utils/prepareChatFile';
import './WebChat.css';

const TOKEN_KEY = 'pif_chat_guest_token';
const DEFAULT_PROPERTY_PROMPT = 'Do you like this property?';
const DEFAULT_PROPERTY_WAIT = 'Chat wait some seconds.';
const DEFAULT_PROPERTY_YES = 'Yes';
const DEFAULT_PROPERTY_YES_MESSAGE = 'Yes, I like this property.';
const DEFAULT_PROPERTY_NO = 'No';
const PROPERTY_PROMPT_WAIT_MS = 3000;

const propertyListingId = (pathname) => {
  const match = String(pathname || '').match(/^\/(?:view\/)?property\/(\d+)(?:\/|$)/);
  return match ? match[1] : '';
};

const toPageProperty = (property) => {
  if (!property) return null;
  const image = property.thumbnail || (Array.isArray(property.slider) ? property.slider.find(Boolean) : '') || '';
  const amount = Number(property.price) > 0 ? property.price : (property.price_from || property.price_to || '');
  const title = property.seo_title
    || property.map_location_name
    || property.location
    || (property.property_id ? `Property #${property.property_id}` : 'Property');
  return {
    id: property.id,
    property_id: property.property_id || property.id,
    title,
    area: property.location || property.address || '',
    image: image || '',
    price: amount === '' || amount == null ? '' : String(amount),
  };
};
const DEFAULT_SUGGESTIONS = [
  'I want more details about this property',
  'Is this property still available?',
  'What is the price?',
  'Can I schedule a visit?',
  'Please call me',
];

const ChatIcon = ({ open }) => (
  <svg className="chat-launcher-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {open ? (
      <path d="M6 6l12 12M18 6L6 18" />
    ) : (
      <>
        <path d="M3 14v-1a9 9 0 0 1 18 0v1" />
        <path d="M21 16.5A2.5 2.5 0 0 1 18.5 19H17v-5h1.5a2.5 2.5 0 0 1 2.5 2.5z" />
        <path d="M3 16.5A2.5 2.5 0 0 0 5.5 19H7v-5H5.5A2.5 2.5 0 0 0 3 16.5z" />
      </>
    )}
  </svg>
);

const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path d={dir === 'left' ? 'M14.5 6.5 L9 12 l5.5 5.5' : 'M9.5 6.5 L15 12 l-5.5 5.5'} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const StatusMark = ({ status }) => {
  if (!status || status === 'pending') {
    return <span className="msg-status" aria-label="Sending"><i className="msg-dot" /></span>;
  }
  const seen = status === 'seen';
  const delivered = status === 'delivered' || seen;
  return (
    <span className={`msg-status${seen ? ' is-seen' : ''}`} aria-label={seen ? 'Seen' : delivered ? 'Delivered' : 'Sent'}>
      <svg viewBox="0 0 18 12" width="16" height="11" aria-hidden="true">
        <path d="M1.2 6.2 L4.4 9.4 L10.2 2.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        {delivered && <path d="M6.2 6.2 L9.4 9.4 L16.2 2.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />}
      </svg>
    </span>
  );
};

const RobotAvatar = ({ size = 42 }) => (
  <svg width={size} height={size} viewBox="0 0 62 62" aria-hidden="true">
    <circle cx="31" cy="31" r="31" fill="#F59E0B" />
    <rect x="15" y="20" width="32" height="27" rx="9" fill="#f0f0f0" />
    <rect x="17" y="22" width="28" height="23" rx="8" fill="#FFFFFF" />
    <rect x="19" y="27" width="9" height="9" rx="3" fill="#0ea5e9" />
    <rect x="34" y="27" width="9" height="9" rx="3" fill="#0ea5e9" />
    <circle cx="21.5" cy="29" r="2.2" fill="#FFFFFF" />
    <circle cx="36.5" cy="29" r="2.2" fill="#FFFFFF" />
    <path d="M23 37 Q31 41.5 39 37" stroke="#555" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <line x1="24" y1="20" x2="21" y2="12" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
    <circle cx="20.5" cy="11" r="2.5" fill="#9ca3af" />
    <line x1="38" y1="20" x2="41" y2="12" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
    <circle cx="41.5" cy="11" r="2.5" fill="#9ca3af" />
    <rect x="10" y="28" width="6" height="11" rx="3" fill="#d1d5db" />
    <rect x="46" y="28" width="6" height="11" rx="3" fill="#d1d5db" />
  </svg>
);

const mediaUrl = (path) => {
  if (!path) return '';
  const value = String(path);
  if (value.startsWith('http') || value.startsWith('blob:') || value.startsWith('data:')) return value;
  return `${BASE_URL}/${value.replace(/^\/+/, '')}`;
};

const formatPrice = (value) => {
  if (value == null || value === '') return '';
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return String(value);
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
};

const messageStatus = (message) => (String(message.id).startsWith('local') ? 'pending' : (message.status || 'sent'));

const PropertyCardMessage = ({ message, mine }) => {
  const image = mediaUrl(message.property_image);
  const href = `/property/${message.listing_id || message.property_id}`;
  return (
    <article className={`prop-card${mine ? ' prop-card--mine' : ''}`}>
      {image ? <img src={image} alt="" /> : <div className="prop-card__photo" />}
      <div className="prop-card__body">
        <strong>#{message.property_id}</strong>
        <span>{message.property_title || message.body}</span>
        <em>{[message.property_location, formatPrice(message.property_price)].filter(Boolean).join(' · ')}</em>
      </div>
      <a className="prop-card__view" href={href} target="_blank" rel="noreferrer" aria-label="View property">
        <FiEye />
      </a>
      {mine && <StatusMark status={messageStatus(message)} />}
    </article>
  );
};

const chatHeaders = () => {
  const headers = {};
  const auth = getAuthToken();
  const guest = localStorage.getItem(TOKEN_KEY);
  if (auth) headers.Authorization = `Bearer ${auth}`;
  if (guest) headers['x-chat-token'] = guest;
  return headers;
};

const withProperty = (message, older) => {
  if (!older?.property_id || message?.property_id) return message;
  return {
    ...message,
    property_id: older.property_id,
    listing_id: older.listing_id,
    property_title: older.property_title,
    property_location: older.property_location,
    property_image: older.property_image,
    property_price: older.property_price,
  };
};

const mergeMessages = (current, incoming) => {
  const previous = new Map(current.filter((item) => item?.id != null).map((item) => [item.id, item]));
  const map = new Map();
  incoming.forEach((message) => {
    if (message?.id == null) return;
    map.set(message.id, withProperty(message, previous.get(message.id)));
  });
  current.forEach((message) => {
    if (message?.id != null && String(message.id).startsWith('local') && !map.has(message.id)) {
      map.set(message.id, message);
    }
  });
  return [...map.values()].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
};

const WebChat = () => {
  const location = useLocation();
  const listingId = propertyListingId(location.pathname);
  const [open, setOpen] = useState(() => {
    try {
      if (sessionStorage.getItem('pif_chat_reopen') !== '1') return false;
      sessionStorage.removeItem('pif_chat_reopen');
      return Boolean(getAuthToken());
    } catch {
      return false;
    }
  });
  const [needsProfile, setNeedsProfile] = useState(false);
  const [chatAllowed, setChatAllowed] = useState(true);
  const [partnerChats, setPartnerChats] = useState([]);
  const [activePartnerId, setActivePartnerId] = useState(null);
  const [partnerMessages, setPartnerMessages] = useState([]);
  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const [mailMinutes, setMailMinutes] = useState(10);
  const [greeting, setGreeting] = useState('Hello! How can we help you today?');
  const [propertyPrompt, setPropertyPrompt] = useState(DEFAULT_PROPERTY_PROMPT);
  const [propertyWait, setPropertyWait] = useState(DEFAULT_PROPERTY_WAIT);
  const [propertyYes, setPropertyYes] = useState(DEFAULT_PROPERTY_YES);
  const [propertyYesMessage, setPropertyYesMessage] = useState(DEFAULT_PROPERTY_YES_MESSAGE);
  const [propertyNo, setPropertyNo] = useState(DEFAULT_PROPERTY_NO);
  const [propertyPromptReady, setPropertyPromptReady] = useState(false);
  const [propertyOfferClosed, setPropertyOfferClosed] = useState(false);
  const [signedIn, setSignedIn] = useState(() => Boolean(getAuthToken()));
  const [pageProperty, setPageProperty] = useState(null);
  const [suggestions, setSuggestions] = useState(DEFAULT_SUGGESTIONS);
  const [propertyOpen, setPropertyOpen] = useState(false);
  const [draftProperty, setDraftProperty] = useState(null);
  const [propertyQuery, setPropertyQuery] = useState('');
  const [propertyResults, setPropertyResults] = useState([]);
  const [pane, setPane] = useState(0);
  const [aiText, setAiText] = useState('');
  const [aiSending, setAiSending] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    { id: 'ai-hello', sender: 'ai', text: "Hi! I'm your PIF AI Agent.\n\nAsk me anything about properties — buy, rent, projects, or urgent deals.", properties: [] },
  ]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [photo, setPhoto] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [zoom, setZoom] = useState('');
  const [dragging, setDragging] = useState(false);
  const photoRef = useRef(null);
  const stickToBottom = useRef(true);
  const listRef = useRef(null);
  const aiListRef = useRef(null);
  const dragRef = useRef(null);
  const typingSent = useRef(0);
  const openRef = useRef(false);
  const promptedListing = useRef('');

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const sync = () => setSignedIn(Boolean(getAuthToken()));
    sync();
    const timer = window.setInterval(sync, 1000);
    window.addEventListener('focus', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!listingId) {
      const previous = promptedListing.current;
      promptedListing.current = '';
      setPageProperty(null);
      if (previous) {
        setDraftProperty((current) => (
          current && String(current.id) === String(previous) ? null : current
        ));
      }
      return undefined;
    }
    promptedListing.current = listingId;
    setPropertyOfferClosed(false);
    let cancelled = false;
    axios.get(`${API_URL}/property/${listingId}`)
      .then((res) => {
        if (cancelled) return;
        setPageProperty(toPageProperty(res.data));
      })
      .catch(() => {
        if (!cancelled) setPageProperty(null);
      });
    return () => { cancelled = true; };
  }, [listingId]);

  const applyPayload = useCallback((data) => {
    if (!data) return;
    if (typeof data.mail_time_minutes === 'number') setMailMinutes(data.mail_time_minutes);
    if (data.greeting) setGreeting(data.greeting);
    if (data.property_prompt) setPropertyPrompt(data.property_prompt);
    if (data.property_wait) setPropertyWait(data.property_wait);
    if (data.property_yes) setPropertyYes(data.property_yes);
    if (data.property_yes_message) setPropertyYesMessage(data.property_yes_message);
    if (data.property_no) setPropertyNo(data.property_no);
    if (Array.isArray(data.suggestions)) setSuggestions(data.suggestions);
    setNeedsProfile(!!data.needs_profile);
    if (typeof data.chat_access === 'boolean') setChatAllowed(data.chat_access);
    if (Array.isArray(data.messages)) {
      setMessages((prev) => mergeMessages(prev.filter((item) => item.id), data.messages));
    }
    setTyping(!!data.thread?.admin_typing);
    if (!openRef.current) setUnread(data.thread?.user_unread || 0);
  }, []);

  const loadThread = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/chat/thread`, { headers: chatHeaders() });
      applyPayload(res.data);
      if (openRef.current && res.data?.thread) {
        axios.post(`${API_URL}/chat/read`, {}, { headers: chatHeaders() }).catch(() => {});
      }
    } catch {
      /* keep the panel usable if a poll fails */
    }
  }, [applyPayload]);

  useEffect(() => {
    loadThread();
    const timer = setInterval(loadThread, open ? 1000 : 8000);
    return () => clearInterval(timer);
  }, [open, loadThread]);

  useEffect(() => {
    if (!open || !getAuthToken()) {
      setPartnerChats([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/chat/partner-threads`, { headers: chatHeaders() });
        if (!cancelled) setPartnerChats(Array.isArray(res.data?.threads) ? res.data.threads : []);
      } catch {
        /* partner chats can retry on the next poll */
      }
    };
    load();
    const timer = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !activePartnerId) {
      setPartnerMessages([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/chat/partner-threads/${activePartnerId}/messages`, { headers: chatHeaders() });
        if (!cancelled) setPartnerMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
      } catch {
        /* keep the last partner messages */
      }
    };
    load();
    const timer = setInterval(load, 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open, activePartnerId]);

  useEffect(() => {
    if (!open) return;
    setUnread(0);
  }, [open]);

  useEffect(() => {
    if (open) stickToBottom.current = true;
  }, [open]);

  useEffect(() => {
    if (!open || !listingId || !signedIn) {
      setPropertyPromptReady(false);
      return undefined;
    }
    setPropertyPromptReady(false);
    const timer = setTimeout(() => setPropertyPromptReady(true), PROPERTY_PROMPT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [open, listingId, signedIn]);

  useEffect(() => {
    const node = listRef.current;
    if (!open || !node || !stickToBottom.current) return undefined;
    const pin = () => {
      const offer = node.querySelector('.web-chat-row--property-offer');
      if (offer) {
        node.scrollTop = Math.max(0, offer.offsetTop - 8);
        return;
      }
      node.scrollTop = node.scrollHeight;
    };
    pin();
    const frame = requestAnimationFrame(pin);
    return () => cancelAnimationFrame(frame);
  }, [messages, typing, open, listingId, pageProperty, propertyPrompt, propertyPromptReady]);

  const onLogScroll = () => {
    const node = listRef.current;
    if (!node) return;
    stickToBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 72;
  };

  useEffect(() => {
    if (!propertyOpen) return undefined;
    const q = propertyQuery.trim();
    if (q.length < 1) {
      setPropertyResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/chat/properties`, { params: { q }, headers: chatHeaders() });
        setPropertyResults(Array.isArray(res.data?.properties) ? res.data.properties : []);
      } catch {
        setPropertyResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [propertyQuery, propertyOpen]);

  const visibleMessages = useMemo(() => {
    const adminReplied = messages.some((message) => message.sender === 'admin');
    const isGreeting = (message) => (
      message.kind === 'greeting'
      || message.id === 'greeting-local'
      || (message.sender === 'system' && message.body === greeting)
    );
    const rows = messages.filter((message) => {
      const waitNote = message.kind === 'wait' || /receive a reply within/i.test(message.body || '');
      return !(adminReplied && waitNote);
    });
    const chatStarted = rows.some((message) => message.sender === 'user' || message.sender === 'admin');
    if (chatStarted || listingId) return rows.filter((message) => !isGreeting(message));
    const withoutGreeting = rows.filter((message) => !isGreeting(message));
    const greetingRow = rows.find((message) => message.kind === 'greeting') || {
      id: 'greeting-local',
      sender: 'system',
      kind: 'greeting',
      body: greeting,
    };
    return [greetingRow, ...withoutGreeting];
  }, [messages, greeting, listingId]);

  const matchedSuggestions = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (q.length < 1) return [];
    return suggestions.filter((item) => item.toLowerCase().includes(q) && item.toLowerCase() !== q).slice(0, 4);
  }, [text, suggestions]);

  const startSession = async (event) => {
    event.preventDefault();
    setError('');
    setSending(true);
    const pendingText = text.trim();
    const pendingDraft = draftProperty;
    const pendingPhoto = photo;
    try {
      const res = await axios.post(`${API_URL}/chat/session`, profile, { headers: chatHeaders() });
      if (res.data?.token) localStorage.setItem(TOKEN_KEY, res.data.token);
      setNeedsProfile(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start the chat');
      setSending(false);
      return;
    }
    setSending(false);
    if (pendingText || pendingDraft || pendingPhoto) {
      await sendMessage(null, { body: pendingText, draft: pendingDraft, picture: pendingPhoto });
    } else {
      await loadThread();
    }
  };

  const clearPhoto = () => {
    setPhoto((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  const attachChatFile = async (file) => {
    if (!file) return;
    try {
      const next = await prepareChatFile(file);
      setPhoto((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return next;
      });
      setError('');
    } catch (err) {
      setError(err.message || 'Could not prepare that file');
    }
  };

  const pickPhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    attachChatFile(file);
  };

  const onDropFile = (event) => {
    event.preventDefault();
    setDragging(false);
    attachChatFile(event.dataTransfer.files?.[0]);
  };

  const beginEdit = (message) => {
    clearPhoto();
    setEditingId(message.id);
    setText(message.body || '');
  };

  const sendMessage = async (event, overrides = {}) => {
    event?.preventDefault?.();
    if (activePartnerId && !overrides.body && !editingId) {
      const body = text.trim();
      if (!body || sending) return;
      setSending(true);
      setError('');
      setText('');
      try {
        const res = await axios.post(
          `${API_URL}/chat/partner-threads/${activePartnerId}/messages`,
          { body },
          { headers: chatHeaders() },
        );
        setPartnerMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
      } catch (err) {
        setText(body);
        if (err.response?.data?.chat_access === false) setChatAllowed(false);
        setError(err.response?.data?.error || 'Message was not sent');
      } finally {
        setSending(false);
      }
      return;
    }
    const body = String(overrides.body ?? text).trim();
    const draft = overrides.draft === undefined ? draftProperty : overrides.draft;
    const picture = overrides.picture === undefined ? photo : overrides.picture;
    if (editingId) {
      if (!body || sending) return;
      setSending(true);
      setError('');
      try {
        const res = await axios.patch(`${API_URL}/chat/messages/${editingId}`, { text: body }, { headers: chatHeaders() });
        if (Array.isArray(res.data?.messages)) setMessages(res.data.messages);
        setEditingId(null);
        setText('');
      } catch (err) {
        setError(err.response?.data?.error || 'Could not edit the message');
      } finally {
        setSending(false);
      }
      return;
    }
    if ((!body && !draft && !picture) || sending) return;
    stickToBottom.current = true;
    setSending(true);
    setError('');
    setText('');
    setDraftProperty(null);
    setPhoto(null);
    setPropertyOpen(false);
    const optimistic = {
      id: `local-${Date.now()}`,
      sender: 'user',
      kind: body ? 'text' : (picture?.kind === 'pdf' ? 'file' : (picture ? 'image' : 'property')),
      status: 'pending',
      body: body || draft?.title || '',
      image: picture?.kind === 'pdf' ? '' : (picture?.url || ''),
      file_name: picture?.name || '',
      property_id: draft?.property_id || null,
      listing_id: draft?.id || null,
      property_title: draft?.title || '',
      property_location: draft?.area || '',
      property_image: draft?.image || '',
      property_price: draft?.price || '',
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const form = new FormData();
      form.append('text', body);
      if (draft?.property_id) form.append('property_id', String(draft.property_id));
      if (picture?.file) form.append('image', picture.file);
      const res = await axios.post(`${API_URL}/chat/messages`, form, { headers: chatHeaders() });
      if (picture?.url) URL.revokeObjectURL(picture.url);
      if (Array.isArray(res.data?.messages)) {
        const rows = res.data.messages.map((item) => ({ ...item }));
        if (draft) {
          const match = [...rows].reverse().find((item) => item.sender === 'user' && item.body === (body || draft.title));
          if (match && !match.property_id) {
            match.property_id = draft.property_id;
            match.listing_id = draft.id;
            match.property_title = draft.title;
            match.property_location = draft.area || '';
            match.property_image = draft.image || '';
            match.property_price = draft.price || '';
          }
        }
        setMessages(rows);
      }
      setTyping(false);
      typingSent.current = 0;
      axios.post(`${API_URL}/chat/typing`, { typing: false }, { headers: chatHeaders() }).catch(() => {});
      if (listingId) setPropertyOfferClosed(true);
    } catch (err) {
      setMessages((prev) => prev.filter((item) => item.id !== optimistic.id));
      setText(body);
      setDraftProperty(draft);
      setPhoto(picture);
      if (err.response?.data?.needs_profile) setNeedsProfile(true);
      if (err.response?.data?.chat_access === false) setChatAllowed(false);
      if (listingId && body === propertyYesMessage) setPropertyOfferClosed(false);
      setError(err.response?.data?.error || 'Message was not sent');
    } finally {
      setSending(false);
    }
  };

  const answerProperty = (answer) => {
    const draft = pageProperty ? {
      id: pageProperty.id,
      property_id: pageProperty.property_id,
      title: pageProperty.title,
      area: pageProperty.area,
      image: pageProperty.image,
      price: pageProperty.price,
    } : null;
    setPane(0);
    setEditingId(null);
    setPropertyOfferClosed(true);
    if (!getAuthToken()) {
      setDraftProperty(draft);
      setText(answer);
      return;
    }
    sendMessage(null, { body: answer, draft, picture: null });
  };

  const onType = (value) => {
    setText(value);
    const now = Date.now();
    const active = value.trim().length > 0;
    if (active && now - typingSent.current < 1500) return;
    if (!active && typingSent.current === 0) return;
    typingSent.current = active ? now : 0;
    axios.post(`${API_URL}/chat/typing`, { typing: active }, { headers: chatHeaders() }).catch(() => {});
  };

  const onDragStart = (event) => {
    if (event.target.closest('input, textarea, button, a')) return;
    dragRef.current = event.clientX;
  };

  const onDragEnd = (event) => {
    if (dragRef.current == null) return;
    const delta = event.clientX - dragRef.current;
    dragRef.current = null;
    if (delta < -48) setPane(1);
    else if (delta > 48) setPane(0);
  };

  const sendAi = async (event, preset) => {
    event?.preventDefault?.();
    const body = String(preset || aiText).trim();
    if (!body || aiSending) return;
    setAiText('');
    setAiMessages((prev) => [...prev, { id: `u-${Date.now()}`, sender: 'user', text: body, properties: [] }]);
    setAiSending(true);
    try {
      const res = await axios.post(`${API_URL}/ai/chat`, { message: body });
      const properties = (Array.isArray(res.data?.properties) ? res.data.properties : []).map((item) => ({
        listing_id: item.id,
        property_id: item.property_id || item.id,
        property_title: item.seo_title || item.location || item.name || `Property #${item.id}`,
        property_location: item.location || item.address || item.city || '',
        property_image: item.thumbnail || item.slider?.[0] || '',
        property_price: item.price || '',
      }));
      setAiMessages((prev) => [...prev, {
        id: `a-${Date.now()}`,
        sender: 'ai',
        text: res.data?.replyText || 'I could not find a match. Try a city, BHK, or rent/sale.',
        properties,
      }]);
    } catch {
      setAiMessages((prev) => [...prev, {
        id: `e-${Date.now()}`,
        sender: 'ai',
        text: 'Trouble connecting. Please try again.',
        properties: [],
      }]);
    } finally {
      setAiSending(false);
    }
  };

  useEffect(() => {
    if (!open || pane !== 1 || !aiListRef.current) return;
    aiListRef.current.scrollTop = aiMessages.length > 1 ? aiListRef.current.scrollHeight : 0;
  }, [aiMessages, aiSending, open, pane]);

  const chooseProperty = (item) => {
    setDraftProperty(item);
    setPropertyOpen(false);
    setPropertyQuery('');
    setError('');
  };

  return (
    <div className="web-chat">
      {open && (
        <section className="web-chat-panel" role="dialog" aria-label={pane === 1 ? 'PIF AI Agent' : 'Chat with us'}>
          <header className="web-chat-panel__head">
            {pane === 1 ? (
              <div className="web-chat-agent">
                <span className="web-chat-agent__avatar">
                  <RobotAvatar />
                  <i />
                </span>
                <div>
                  <strong>PIF AI Agent</strong>
                  <p>Online · Always ready</p>
                </div>
              </div>
            ) : (
              <div>
                <strong>Chat with us</strong>
                <p>We usually reply within {mailMinutes} minutes</p>
              </div>
            )}
            <button type="button" className="web-chat-icon-btn" onClick={() => setOpen(false)} aria-label="Close chat">
              <FiX />
            </button>
          </header>
          <div className="web-chat-swipe">
            <button type="button" className="web-chat-swipe__btn" onClick={() => setPane(0)} disabled={pane === 0} aria-label="Swipe to support chat">
              <Chevron dir="left" />
            </button>
            <div className="web-chat-swipe__rail" role="tablist" aria-label="Chat mode">
              <span className="web-chat-swipe__thumb" style={{ transform: `translateX(${pane * 100}%)` }} />
              <button type="button" role="tab" aria-selected={pane === 0} className={pane === 0 ? 'is-on' : ''} onClick={() => setPane(0)}>Support</button>
              <button type="button" role="tab" aria-selected={pane === 1} className={pane === 1 ? 'is-on' : ''} onClick={() => setPane(1)}>AI Agent</button>
            </div>
            <button type="button" className="web-chat-swipe__btn" onClick={() => setPane(1)} disabled={pane === 1} aria-label="Swipe to PIF AI Agent">
              <Chevron dir="right" />
            </button>
          </div>

          <div
            className="web-chat-pager"
            onPointerDown={onDragStart}
            onPointerUp={onDragEnd}
            onPointerCancel={() => { dragRef.current = null; }}
          >
            <div className="web-chat-track" style={{ transform: `translateX(-${pane * 50}%)` }}>
              <div
                className={`web-chat-pane${dragging ? ' is-dropping' : ''}`}
                onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDropFile}
              >
          {partnerChats.length > 0 && (
            <div className="web-chat-partner-picks">
              <button type="button" className={!activePartnerId ? 'is-on' : ''} onClick={() => setActivePartnerId(null)}>Support</button>
              {partnerChats.map((chat) => (
                <button
                  type="button"
                  key={chat.partner_id}
                  className={activePartnerId === chat.partner_id ? 'is-on' : ''}
                  onClick={() => setActivePartnerId(chat.partner_id)}
                >
                  {chat.name}
                </button>
              ))}
            </div>
          )}
          <div className="web-chat-log" ref={listRef} onScroll={onLogScroll}>
            {(activePartnerId ? partnerMessages : visibleMessages).map((message) => {
              const mine = message.sender === 'user';
              const manageable = mine && !message.deleted && message.kind !== 'greeting' && message.kind !== 'wait';
              return (
                <div key={message.id} className={`web-chat-row web-chat-row--${message.sender}`}>
                  <div className={`web-chat-bubble web-chat-bubble--${message.sender}${message.property_id ? ' has-property' : ''}${message.deleted ? ' is-deleted' : ''}`}>
                    {message.deleted ? (
                      <span className="msg-deleted">This message was deleted</span>
                    ) : (
                      <>
                        {!mine && message.sender_name ? <span className="web-chat-who">{message.sender_name}</span> : null}
                        {isPdfAttachment(message) ? (
                          message.image ? (
                            <a className="chat-pdf" href={mediaUrl(message.image)} target="_blank" rel="noreferrer">{message.file_name || 'PDF'}</a>
                          ) : (
                            <span className="chat-pdf">{message.file_name || 'PDF'}</span>
                          )
                        ) : message.image ? (
                          <button type="button" className="chat-photo-btn" onClick={() => setZoom(mediaUrl(message.image))}>
                            <img src={mediaUrl(message.image)} alt="" />
                          </button>
                        ) : null}
                        {message.property_id && <PropertyCardMessage message={message} />}
                        {message.kind !== 'property' && message.body ? <span>{message.body}</span> : null}
                        {message.edited_at && <small className="msg-edited">Edited</small>}
                      </>
                    )}
                    {mine && !message.deleted && <StatusMark status={messageStatus(message)} />}
                  </div>
                  {manageable && message.kind !== 'property' && (
                    <div className="msg-actions">
                      <button type="button" onClick={() => beginEdit(message)}>Edit</button>
                    </div>
                  )}
                </div>
              );
            })}
            {signedIn && listingId && !activePartnerId && !propertyOfferClosed && !propertyPromptReady && (
              <div className="web-chat-row web-chat-row--system web-chat-row--property-offer">
                <div className="web-chat-bubble web-chat-bubble--system">{propertyWait}</div>
              </div>
            )}
            {signedIn && listingId && !activePartnerId && !propertyOfferClosed && propertyPromptReady && (
              <div className="web-chat-row web-chat-row--admin web-chat-row--property-offer">
                <div className="web-chat-bubble web-chat-bubble--admin has-property">
                  <span>{propertyPrompt}</span>
                  {pageProperty && (
                    <PropertyCardMessage message={{
                      property_id: pageProperty.property_id,
                      listing_id: pageProperty.id,
                      property_title: pageProperty.title,
                      property_location: pageProperty.area,
                      property_image: pageProperty.image,
                      property_price: pageProperty.price,
                    }} />
                  )}
                  <div className="chat-yes-no">
                    <button type="button" onClick={() => answerProperty(propertyYesMessage)} disabled={sending}>{propertyYes}</button>
                    <button type="button" onClick={() => answerProperty(propertyNo)} disabled={sending}>{propertyNo}</button>
                  </div>
                </div>
              </div>
            )}
            {typing && <div className="web-chat-typing">Admin is typing…</div>}
          </div>
          {!chatAllowed ? (
            <p className="web-chat-form">Chat access is not enabled for this account.</p>
          ) : needsProfile ? (
            <form className="web-chat-form" onSubmit={startSession}>
              <p>
                Tell us how to reach you, then send your message. Or{' '}
                <Link
                  to="/login"
                  state={{ from: location }}
                  onClick={() => {
                    try { sessionStorage.setItem('pif_chat_reopen', '1'); } catch { /* ignore */ }
                  }}
                >
                  log in
                </Link>
                {' '}to continue.
              </p>
              {text.trim() && <p className="web-chat-ready">Ready to send: {text.trim()}</p>}
              <label>
                Name
                <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required />
              </label>
              <label>
                Email
                <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} required />
              </label>
              <label>
                Phone
                <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} required />
              </label>
              {error && <p className="web-chat-error">{error}</p>}
              <button type="submit" className="web-chat-send" disabled={sending}>Continue</button>
            </form>
          ) : (
            <>
              {propertyOpen && (
                <div className="web-chat-property-picker">
                  <input
                    value={propertyQuery}
                    onChange={(e) => setPropertyQuery(e.target.value)}
                    placeholder="Search by property ID or area"
                    aria-label="Search properties"
                  />
                  <div className="web-chat-property-results">
                    {propertyResults.length === 0 && propertyQuery.trim() && <p>No matching properties.</p>}
                    {propertyResults.map((item) => (
                      <button type="button" key={`${item.id}-${item.property_id}`} className={draftProperty?.property_id === item.property_id ? 'is-picked' : ''} onClick={() => chooseProperty(item)}>
                        {item.image ? <img src={mediaUrl(item.image)} alt="" /> : <span className="prop-card__photo" />}
                        <strong>#{item.property_id}</strong>
                        <span>{item.title}{item.area ? ` · ${item.area}` : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {matchedSuggestions.length > 0 && !editingId && (
                <div className="web-chat-suggestions">
                  {matchedSuggestions.map((item) => (
                    <button type="button" key={item} onClick={() => setText(item)}>{item}</button>
                  ))}
                </div>
              )}
              {draftProperty && (
                <div className="prop-draft">
                  {draftProperty.image ? <img src={mediaUrl(draftProperty.image)} alt="" /> : <span className="prop-card__photo" />}
                  <div>
                    <strong>#{draftProperty.property_id}</strong>
                    <span>{draftProperty.title}</span>
                  </div>
                  <button type="button" onClick={() => setDraftProperty(null)} aria-label="Remove selected property">
                    <FiX />
                  </button>
                </div>
              )}
              {photo?.kind === 'pdf' && (
                <div className="chat-photo-draft chat-file-draft">
                  <span>{photo.name}</span>
                  <button type="button" onClick={clearPhoto} aria-label="Remove PDF">
                    <FiX />
                  </button>
                </div>
              )}
              {photo?.kind === 'image' && (
                <div className="chat-photo-draft">
                  <img src={photo.url} alt="" />
                  <button type="button" onClick={clearPhoto} aria-label="Remove photo">
                    <FiX />
                  </button>
                </div>
              )}
              {dragging && <p className="chat-drop-hint">Drop a photo or PDF to attach it</p>}
              {editingId && (
                <div className="chat-editing">
                  <span>Editing message</span>
                  <button type="button" onClick={() => { setEditingId(null); setText(''); }}>Cancel</button>
                </div>
              )}
              <form className="web-chat-compose" onSubmit={sendMessage}>
                <button
                  type="button"
                  className={`chat-tool chat-tool--icon${propertyOpen ? ' is-on' : ''}`}
                  aria-label="Enter Property ID"
                  onClick={() => setPropertyOpen((value) => !value)}
                >
                  <FiHome aria-hidden="true" />
                  <span className="chat-tool__tip">Enter Property ID</span>
                </button>
                <button type="button" className="chat-tool chat-tool--file" aria-label="Attach a file" onClick={() => photoRef.current?.click()} disabled={!!editingId}>
                  <FiPaperclip aria-hidden="true" />
                </button>
                <input ref={photoRef} type="file" accept="image/*,application/pdf,.pdf" hidden onChange={pickPhoto} />
                <input
                  value={text}
                  onChange={(e) => onType(e.target.value)}
                  placeholder={editingId ? 'Edit your message' : (draftProperty || photo ? 'Add a message, or just send' : 'Write a message')}
                  aria-label="Message"
                />
                <button type="submit" className="web-chat-send" aria-label={editingId ? 'Save edit' : 'Send'} disabled={sending || (!text.trim() && !draftProperty && !photo)}>
                  <SendIcon />
                </button>
              </form>
              {error && <p className="web-chat-error web-chat-error--bar">{error}</p>}
            </>
          )}
              </div>
              <div className="web-chat-pane">
                <div className="web-chat-log" ref={aiListRef}>
                  {aiMessages.map((message) => (
                    <div key={message.id} className={`ai-row ai-row--${message.sender}`}>
                      {message.sender === 'ai' && <span className="ai-avatar"><RobotAvatar size={32} /></span>}
                      <div className="ai-col">
                        <div className={`web-chat-bubble web-chat-bubble--${message.sender === 'user' ? 'user' : 'admin'}`}>
                          <span>{message.text}</span>
                        </div>
                        {message.properties?.length > 0 && (
                          <div className="ai-cards">
                            {message.properties.map((card) => (
                              <PropertyCardMessage key={`${message.id}-${card.property_id}`} message={card} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {aiSending && (
                    <div className="ai-row ai-row--ai">
                      <span className="ai-avatar"><RobotAvatar size={32} /></span>
                      <div className="web-chat-typing">PIF AI Agent is typing…</div>
                    </div>
                  )}
                </div>
                {aiMessages.length < 2 && (
                  <div className="web-chat-suggestions">
                    {['Flats for rent', '2 BHK to buy', 'Urgent properties', 'Commercial space'].map((item) => (
                      <button type="button" key={item} onClick={() => sendAi(null, item)}>{item}</button>
                    ))}
                  </div>
                )}
                <form className="web-chat-compose" onSubmit={sendAi}>
                  <input
                    value={aiText}
                    onChange={(e) => setAiText(e.target.value)}
                    placeholder="Ask about any property"
                    aria-label="Ask the AI agent"
                  />
                  <button type="submit" aria-label="Send" disabled={aiSending || !aiText.trim()}>
                    <SendIcon />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}

      {zoom && (
        <button type="button" className="chat-zoom" onClick={() => setZoom('')} aria-label="Close photo">
          <img src={zoom} alt="" />
        </button>
      )}

      <button
        type="button"
        className={`hca-btn hca-btn--chat${open ? ' is-open' : ''}${listingId ? ' is-property' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? 'Close chat' : 'Open chat'}
        aria-expanded={open}
      >
        <span className="hca-btn__icon"><ChatIcon open={open} /></span>
        <span className="hca-btn__label">Chat</span>
        {unread > 0 && !open && <span className="web-chat-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
    </div>
  );
};

export default WebChat;
